import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useB3Lang } from '@b3/lang';
import { Box } from '@mui/material';

import { B2BAutoCompleteCheckbox } from '@/components';
import B3Filter from '@/components/filter/B3Filter';
import B3Spin from '@/components/spin/B3Spin';
import { B3PaginationTable } from '@/components/table/B3PaginationTable';
import { TableColumnItem } from '@/components/table/B3Table';
import { useMobile, useSort } from '@/hooks';
import {
  getBcOrderStatusType,
  getCompanyData,
  getOrdersCreatedByUser,
  getOrderStatusType,
} from '@/shared/service/b2b';
import { fetchDeliveries } from '@/shared/service/request/alumio';
import { isB2BUserSelector, useAppSelector } from '@/store';
import { CustomerRole } from '@/types';
import { formatDateSap } from '@/utils';

import { DeliveryReleaseStatus, DeliveryTypeStatus } from './components/DeliveryStatus';
import {
  defaultSortKey,
  filterFormConfigsTranslationVariables,
  FilterSearchProps,
  getFilterMoreData,
  getInitFilter,
  sortKeys,
} from './config';
import { DeliveryItemCard } from './DeliveryItemCard';

interface CompanyInfoProps {
  companyId: string;
  companyName: string;
  companyAddress: string;
  companyCountry: string;
  companyState: string;
  companyCity: string;
  companyZipCode: string;
  phoneNumber: string;
  bcId: string;
}

interface ListItem {
  ID?: string;
  CreationDateTime: string;
  DeliveryTypeCodeText: string;
  DeliveryTypeCode: string;
  DeliveryProcessingStatusCodeText: string;
  DeliveryProcessingStatusCode: number;
  ReleaseStatusCodeText: string;
  ReleaseStatusCode: number;
  PickupIndicator: string;
  ShippingPeriod: any;
  StartDateTime: any;

  firstName: string;
  lastName: string;
  orderId: string;
  poNumber?: string;
  money: string;
  totalIncTax: string;
  status: string;
  createdAt: string;
  companyName: string;
  companyInfo?: CompanyInfoProps;
  TypeCodeText: string;

  Item?: {
    ItemSalesOrderReference?: {
      ID?: string;
    };
  }[];
}

interface SearchChangeProps {
  startValue?: string;
  endValue?: string;
  PlacedBy?: string;
  orderStatus?: string | number;
  company?: string;
  deliveryTypeStatus?: string;
  processingStatus?: string;
  releaseStatus?: string;
  deliveryId?: number;
  creationDate?: string;
  deliveryType?: string;
  ID?: number;
}

interface ExtraField {
  fieldName: string;
  fieldValue: string;
}

function Deliveries() {
  const isCompanyOrder = true;
  const b3Lang = useB3Lang();
  const [isMobile] = useMobile();
  const isB2BUser = useAppSelector(isB2BUserSelector);
  const companyB2BId = useAppSelector(({ company }) => company.companyInfo.id);
  const role = useAppSelector(({ company }) => company.customer.role);
  const salesRepCompanyId = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.id);
  const isAgenting = useAppSelector(({ b2bFeatures }) => b2bFeatures.masqueradeCompany.isAgenting);
  const hasAppliedUrlFilter = useRef(false);

  const { order: orderSubViewPermission } = useAppSelector(
    ({ company }) => company.pagesSubsidiariesPermission,
  );

  const { selectCompanyHierarchyId, isEnabledCompanyHierarchy } = useAppSelector(
    ({ company }) => company.companyHierarchyInfo,
  );
  const currentCompanyId =
    role === CustomerRole.SUPER_ADMIN && isAgenting
      ? Number(salesRepCompanyId)
      : Number(companyB2BId);

  const [isRequestLoading, setIsRequestLoading] = useState(false);

  const [allTotal, setAllTotal] = useState(0);

  const [filterData, setFilterData] = useState<Partial<FilterSearchProps> | null>(null);

  const [filterInfo, setFilterInfo] = useState<Array<any>>([]);

  const [getOrderStatuses, setOrderStatuses] = useState<Array<any>>([]);
  const [isAutoRefresh, setIsAutoRefresh] = useState(false);

  const [handleSetOrderBy, order, orderBy] = useSort(
    sortKeys,
    defaultSortKey,
    filterData,
    setFilterData,
  );

  useEffect(() => {
    const search = getInitFilter(isCompanyOrder, isB2BUser);
    if (isB2BUser) {
      search.companyIds = [Number(selectCompanyHierarchyId) || Number(currentCompanyId)];
    }
    setFilterData(search);
    setIsAutoRefresh(true);
    if (role === 100) return;

    const initFilter = async () => {
      const companyId = companyB2BId || salesRepCompanyId;
      const createdByUsers =
        isB2BUser && isCompanyOrder ? await getOrdersCreatedByUser(Number(companyId), 0) : {};

      const orderStatuses = isB2BUser ? await getOrderStatusType() : await getBcOrderStatusType();

      const filterInfo = getFilterMoreData(
        isB2BUser,
        role,
        isCompanyOrder,
        isAgenting,
        createdByUsers,
        orderStatuses,
      );
      setOrderStatuses(orderStatuses);

      const filterInfoWithTranslatedLabel = filterInfo.map((element) => {
        const translatedElement = { ...element };
        translatedElement.label = b3Lang(element.idLang);

        translatedElement.options = translatedElement?.options?.map((option) => {
          return {
            ...option,
            value: String(option.value),
            label: b3Lang(filterFormConfigsTranslationVariables[option.key]),
          };
        });

        return translatedElement;
      });

      setFilterInfo(filterInfoWithTranslatedLabel);
    };

    initFilter();
    // disabling as we only need to run this once and values at starting render are good enough
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectCompanyHierarchyId]);

  const fetchList = useCallback(
    async (
      params: Partial<FilterSearchProps>,
    ): Promise<{ edges: ListItem[]; totalCount: number }> => {
      let data: ListItem[] = [];
      let totalCount = 0;

      try {
        setIsAutoRefresh(false);

        if (!hasAppliedUrlFilter.current) {
          const hash = window.location.hash || '';
          const [path, query] = hash.split('?');
          const searchParams = new URLSearchParams(query);
          const deliveryIdFromUrl = searchParams.get('deliveryId') || '';

          if (deliveryIdFromUrl) {
            params.deliveryId = Number(deliveryIdFromUrl);

            setTimeout(() => {
              const filterTriggerEvent = new CustomEvent('b3-filter-applied', {
                detail: {
                  deliveryId: Number(deliveryIdFromUrl),
                },
              });
              window.dispatchEvent(filterTriggerEvent);
            }, 0);

            searchParams.delete('deliveryId');
            const newHash = searchParams.toString() ? `${path}?${searchParams.toString()}` : path;
            window.location.hash = newHash;
          }

          hasAppliedUrlFilter.current = true;
        }

        const resCompany = await getCompanyData(companyB2BId);

        const accountId = resCompany?.data?.extraFields.find(
          (field: ExtraField) => field.fieldName === 'account_id',
        )?.fieldValue;

        if (!accountId) {
          console.error('Account ID not found in extraFields.');
          return { edges: [], totalCount: 0 };
        }

        const response = await fetchDeliveries(accountId, params, params?.offset, params?.first);

        if (!response) {
          return { edges: [], totalCount: 0 };
        }

        const { __count: count, results = [] } = response;

        totalCount = parseInt(count, 10);

        data = results.filter((delivery: any) => delivery?.SellerParty?.PartyID === '1000');

        setAllTotal(totalCount);
        setIsAutoRefresh(false);

        return {
          edges: data,
          totalCount,
        };
      } catch (error) {
        console.error('Error in fetchList:', error);
        return {
          edges: [],
          totalCount: 0,
        };
      }
    },
    [companyB2BId],
  );

  const navigate = useNavigate();

  const goToDetail = (item: ListItem, index: number) => {
    navigate(`/deliveryDetail/${item.ID}`, {
      state: {
        currentIndex: index,
        searchParams: filterData,
        totalCount: allTotal,
        isCompanyOrder,
        beginDateAt: filterData?.beginDateAt,
        endDateAt: filterData?.endDateAt,
      },
    });
  };

  const columnAllItems: TableColumnItem<ListItem>[] = [
    {
      key: 'ID',
      title: b3Lang('deliveries.delivery'),
      width: '10%',
      isSortable: true,
    },
    {
      key: 'deliveryNumber',
      title: b3Lang('deliveries.orderId'),
      isSortable: true,
      render: (item: ListItem) => (
        <Box
          sx={{
            color: '#000000',
            cursor: 'pointer',
            ':hover': {
              textDecoration: 'underline',
            },
          }}
          onClick={(event) => {
            if (!item.Item?.[0]?.ItemSalesOrderReference?.ID) return;
            event.stopPropagation();
            navigate(`/company-orders?orderId=${item.Item?.[0]?.ItemSalesOrderReference?.ID}`);
          }}
        >
          {item.Item?.length ? item.Item[0]?.ItemSalesOrderReference?.ID : '-'}
        </Box>
      ),
      width: '12%',
    },
    {
      key: 'deliveryType',
      title: b3Lang('deliveries.deliveryType'),
      width: '10%',
      isSortable: true,
      render: (item: ListItem) => {
        return <DeliveryTypeStatus code={item.DeliveryTypeCode} />;
      },
    },
    {
      key: 'releaseStatus',
      title: b3Lang('deliveries.releaseStatus'),
      render: (item: ListItem) => {
        return <DeliveryReleaseStatus code={item.ReleaseStatusCode} />;
      },
      width: '10%',
      isSortable: true,
    },
    {
      key: 'shippingDate',
      title: b3Lang('deliveries.shippingDate'),
      width: '10%',
      isSortable: false,
      render: (item: ListItem) => `${formatDateSap(item.ShippingPeriod.StartDateTime)}`,
    },
  ];

  const getColumnItems = () => {
    const getNewColumnItems = columnAllItems.filter((item: { key: string }) => {
      const { key } = item;
      if (!isB2BUser && key === 'companyName') return false;
      if ((!isB2BUser || (Number(role) === 3 && !isAgenting)) && key === 'placedBy') return false;
      if (key === 'companyId' && isB2BUser && (Number(role) !== 3 || isAgenting)) return false;
      if (
        (key === 'companyId' || key === 'placedBy') &&
        !(Number(role) === 3 && !isAgenting) &&
        !isCompanyOrder
      )
        return false;
      return true;
    });

    return getNewColumnItems;
  };

  const handleChange = (key: string, value: string) => {
    if (key === 'search') {
      setFilterData({
        ...filterData,
        q: value,
      });
    }
  };

  const handleFilterChange = (value: SearchChangeProps) => {
    let currentStatus = value?.orderStatus || '';
    if (currentStatus) {
      const originStatus = getOrderStatuses.find(
        (status) => status.customLabel === currentStatus || status.systemLabel === currentStatus,
      );

      currentStatus = originStatus?.systemLabel || currentStatus;
    }

    const search: Partial<FilterSearchProps> = {
      beginDateAt: value?.startValue || null,
      endDateAt: value?.endValue || null,
      createdBy: value?.PlacedBy || '',
      statusCode: currentStatus,
      companyName: value?.company || '',
      deliveryTypeStatus: value?.deliveryTypeStatus || '',
      processingStatus: value?.processingStatus || '',
      releaseStatus: value?.releaseStatus || '',
      deliveryId: value?.deliveryId || 0,
      creationDate: value?.creationDate || '',
      deliveryType: value?.deliveryType || '',
      ID: value?.ID || '',
    };
    setFilterData({
      ...filterData,
      ...search,
    });
  };

  const columnItems = getColumnItems();

  const handleSelectCompanies = (company: number[]) => {
    const newCompanyIds = company.includes(-1) ? [] : company;

    setFilterData({
      ...filterData,
      companyIds: newCompanyIds,
    });
  };

  const memoizedSearchParams = useMemo(() => filterData || {}, [filterData]);

  return (
    <B3Spin isSpinning={isRequestLoading}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          flex: 1,
          width: '100%',
        }}
      >
        <Box
          sx={{
            width: isMobile ? '100%' : 'auto',
            display: 'flex',
            justifyContent: 'flex-start',
            alignItems: isMobile ? 'flex-start' : 'center',
            flexDirection: isMobile ? 'column' : 'row',

            '& > div': {
              width: isMobile ? '100%' : 'auto',
            },
          }}
        >
          {isEnabledCompanyHierarchy && orderSubViewPermission && (
            <Box sx={{ mr: isMobile ? 0 : '10px', mb: '30px' }}>
              <B2BAutoCompleteCheckbox handleChangeCompanyIds={handleSelectCompanies} />
            </Box>
          )}
          <B3Filter
            startPicker={{
              isEnabled: true,
              label: b3Lang('deliveries.creationDateFrom'),
              defaultValue: filterData?.beginDateAt || null,
              pickerKey: 'start',
            }}
            endPicker={{
              isEnabled: true,
              label: b3Lang('deliveries.creationDateTo'),
              defaultValue: filterData?.endDateAt || null,
              pickerKey: 'end',
            }}
            filterMoreInfo={filterInfo}
            handleChange={handleChange}
            handleFilterChange={handleFilterChange}
            pcTotalWidth="100%"
            pcContainerWidth="100%"
            pcSearchContainerWidth="100%"
          />
        </Box>

        <B3PaginationTable
          columnItems={columnItems}
          rowsPerPageOptions={[10, 20, 30]}
          getRequestList={fetchList}
          searchParams={memoizedSearchParams}
          isCustomRender={false}
          requestLoading={setIsRequestLoading}
          tableKey="ID"
          pageType="orderListPage" // TODO: Change this to a proper naming?
          isAutoRefresh={isAutoRefresh}
          sortDirection={order}
          orderBy={orderBy}
          sortByFn={handleSetOrderBy}
          renderItem={(row: ListItem, index?: number) => (
            <DeliveryItemCard
              key={row.orderId}
              item={row}
              index={index}
              allTotal={allTotal}
              filterData={filterData}
              isCompanyOrder={isCompanyOrder}
            />
          )}
          onClickRow={(item: ListItem, index?: number) => {
            if (index !== undefined) {
              goToDetail(item, index);
            }
          }}
          hover
        />
      </Box>
    </B3Spin>
  );
}

export default Deliveries;
